import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface PujaPackage {
  id: string;
  name: string;
  price: number;
  inclusions: string[];
  durationHours: number;
  numberOfPandits: number;
}

export interface Puja {
  id: string;
  name: string;
  slug: string;
  category: string;
  categorySlug: string;
  shortDescription: string;
  description: string;
  significance: string;
  process: string;
  startingPrice: number;
  rating: number;
  reviewCount: number;
  isFeatured: boolean;
  isLive: boolean;
  thumbnailUrl: string;
  packages: PujaPackage[];
  availableCities: string[];
}

export interface CartItem {
  id: string;
  puja: Puja;
  selectedPackage: PujaPackage;
  scheduledDate: string;
  devoteeName: string;
  gotra?: string;
  sankalp?: string;
  city: string;
}

@Injectable({
  providedIn: 'root'
})
export class PlatformService {
  
  // ── Mock Categories ──
  categories = [
    { name: 'Temple Specific Puja', slug: 'temple-specific', icon: 'fa-gopuram' },
    { name: 'God Specific Puja', slug: 'god-specific', icon: 'fa-om' },
    { name: 'Festival Specific Puja', slug: 'festival-specific', icon: 'fa-dharmachakra' },
    { name: 'Dosh Nirvan Puja', slug: 'dosh-nivaran', icon: 'fa-star-of-david' },
    { name: 'Spiritual Yatra', slug: 'spiritual-yatra', icon: 'fa-route' },
    { name: 'Abhishekam', slug: 'abhishekam', icon: 'fa-water' }
  ];

  // ── Mock Cities ──
  cities = ['Varanasi (Kashi)', 'Deoghar (Baidyanath)', 'Ujjain (Mahakal)', 'Bangalore', 'Hyderabad', 'Pune'];

  // ── Mock Pujas Catalog ──
  private pujas: Puja[] = [
    {
      id: 'p1',
      name: 'Maha Rudrabhishek & Aarti at Kashi Vishwanath',
      slug: 'kashi-vishwanath-rudrabhishek',
      category: 'Temple Specific Puja',
      categorySlug: 'temple-specific',
      shortDescription: 'Perform sacred Rudrabhishek puja inside the Kashi Vishwanath temple premises for health, wealth & spiritual liberation.',
      description: 'Rudrabhishek is a sacred Vedic ritual performed to worship Lord Shiva in his Rudra form. During this puja, the Shivalinga is bathed in holy liquids like Panchamrit, honey, milk, and sugarcane juice, accompanied by the powerful chanting of the Sri Rudram. This ritual is known for clearing financial debts, bringing domestic harmony, and removing obstacles.',
      significance: 'Varanasi is the holy abode of Lord Shiva. Performing Rudrabhishek here brings special blessings as it is believed that a single prayer in Kashi is amplified a thousand times.',
      process: '1. Ganesha Puja & Kalash Sthapana\n2. Devotee Sankalp (Name, Gotra & Wish)\n3. Chanting of Sri Rudram\n4. Abhishek with 11 holy substances\n5. Maha Aarti & Pushpanjali',
      startingPrice: 5100,
      rating: 4.9,
      reviewCount: 342,
      isFeatured: true,
      isLive: true,
      thumbnailUrl: 'https://images.unsplash.com/photo-1605649487212-47bdab064df7?auto=format&fit=crop&q=80&w=800',
      availableCities: ['Varanasi (Kashi)'],
      packages: [
        { id: 'pkg1_1', name: 'Standard (1 Pandit)', price: 5100, durationHours: 1.5, numberOfPandits: 1, inclusions: ['Sankalp', 'Standard Prasad', 'Puja Photo'] },
        { id: 'pkg1_2', name: 'Premium (3 Pandits)', price: 11000, durationHours: 2.5, numberOfPandits: 3, inclusions: ['Maha Abhishek', 'Complete Prasad Box', 'Puja Video & Live Stream Link', 'Rudraksha Mala'] },
        { id: 'pkg1_3', name: 'Grand (5 Pandits)', price: 21000, durationHours: 4, numberOfPandits: 5, inclusions: ['Laghu Rudrabhishek', 'Exclusive VIP Prasad Box', 'HD Video Recording', '1-on-1 Live Stream', 'Saffron Shawl'] }
      ]
    },
    {
      id: 'p2',
      name: 'Maha Mrityunjaya Jaap & Dosh Nivaran',
      slug: 'maha-mrityunjaya-jaap',
      category: 'Dosh Nirvan Puja',
      categorySlug: 'dosh-nivaran',
      shortDescription: 'Powerful planetary remedial chanting performed by specialized Vedic scholars for health, longevity, and negative energy clearance.',
      description: 'The Maha Mrityunjaya Mantra is one of the oldest and most powerful mantras in Vedic literature. Performing this chanting puja removes premature death fears, cures chronic diseases, and restores physical wellness. Ideal for planetary shifts, health crises, or resolving chronic blockages.',
      significance: 'Lord Shiva is the ultimate healer. Chanting the Mrityunjaya mantra in Kashi is highly meritorious.',
      process: '1. Ganesha invocation\n2. Sankalp declaration\n3. 11,000 / 21,000 chants of Mrityunjaya Mantra\n4. Havan (Sacred Fire Ritual)\n5. Purnaahuti & Aarti',
      startingPrice: 7500,
      rating: 4.8,
      reviewCount: 198,
      isFeatured: true,
      isLive: false,
      thumbnailUrl: 'https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?auto=format&fit=crop&q=80&w=800',
      availableCities: ['Varanasi (Kashi)', 'Ujjain (Mahakal)', 'Bangalore', 'Pune'],
      packages: [
        { id: 'pkg2_1', name: 'Basic Chanting (11,000 Jaap)', price: 7500, durationHours: 3, numberOfPandits: 3, inclusions: ['Chanting', 'Sankalp Havan', 'Dry Fruits Prasad'] },
        { id: 'pkg2_2', name: 'Standard Chanting (21,000 Jaap)', price: 15000, durationHours: 5, numberOfPandits: 5, inclusions: ['Chanting', 'Havan', 'Premium Prasad Box', 'WhatsApp Video Snippet'] },
        { id: 'pkg2_3', name: 'Grand Chanting (51,000 Jaap)', price: 31000, durationHours: 8, numberOfPandits: 7, inclusions: ['Full Chanting', 'Maha Havan', 'Live Broadcast', 'Grand Prasad Box (incl. Ganga Jal, Rudraksha, Sweets)'] }
      ]
    },
    {
      id: 'p3',
      name: 'Kaal Sarp Dosh Puja at Baidyanath Jyotirlinga',
      slug: 'kaal-sarp-dosh-puja',
      category: 'Dosh Nirvan Puja',
      categorySlug: 'dosh-nivaran',
      shortDescription: 'Remove career, marriage & health blockages caused by the Rahu-Ketu Kaal Sarp alignment.',
      description: 'When all seven planets are positioned between Rahu and Ketu in an astrological chart, it causes Kaal Sarp Dosh, leading to continuous struggles. This puja uses Vedic metal snake idols, special chants, and offering of milk at the Jyotirlinga shrine to neutralize planetary curses.',
      significance: 'Deoghar / Baidyanath Jyotirlinga is the home of Vaidyanatha (Lord of Healers), making it a powerful center for dosha removals.',
      process: '1. Rahu & Ketu Puja\n2. Nag Puja with silver offerings\n3. Kaal Sarp mantra chanting\n4. Visarjan of snake replicas in holy water\n5. Baidyanath temple Darshan',
      startingPrice: 4500,
      rating: 4.7,
      reviewCount: 167,
      isFeatured: false,
      isLive: false,
      thumbnailUrl: 'https://images.unsplash.com/photo-1590076214667-c0f38ed4634b?auto=format&fit=crop&q=80&w=800',
      availableCities: ['Deoghar (Baidyanath)', 'Varanasi (Kashi)', 'Ujjain (Mahakal)'],
      packages: [
        { id: 'pkg3_1', name: 'Eka Pandit (Standard)', price: 4500, durationHours: 2, numberOfPandits: 1, inclusions: ['Metal Nag Puja', 'Sankalp', 'Basic Prasad'] },
        { id: 'pkg3_2', name: 'Vishesh (Multiple Pandits)', price: 8500, durationHours: 3, numberOfPandits: 3, inclusions: ['Detailed Rahu-Ketu Jaap', 'Silver Nag Replicas', 'Live Video Feed', 'Maha Prasad box'] }
      ]
    },
    {
      id: 'p4',
      name: 'Varanasi Ganga Aarti & Special Boat Puja',
      slug: 'ganga-aarti-boat-puja',
      category: 'Spiritual Yatra',
      categorySlug: 'spiritual-yatra',
      shortDescription: 'Experience a holy boat ride alongside the Varanasi ghats and perform deep-daan & Ganga puja with live chanting.',
      description: 'Sail through the holy river Ganges during sunset. Witness the divine Dashashwamedh Ghat Ganga Aarti directly from a private double-decker boat while performing custom river rituals, deep-daan (floating oil lamps), and chanting prayers for the well-being of ancestors.',
      significance: 'Mother Ganga is the purifier of sins. Offering prayers on her waters at dusk is a transcendental experience.',
      process: '1. Private Boat Boarding\n2. Ganga Kalash Puja on Boat\n3. Deep Daan (11 oil lamps)\n4. Viewing Dashashwamedh Aarti\n5. Holy water collection (Ganga Jal prasad)',
      startingPrice: 3500,
      rating: 4.95,
      reviewCount: 456,
      isFeatured: true,
      isLive: true,
      thumbnailUrl: 'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?auto=format&fit=crop&q=80&w=800',
      availableCities: ['Varanasi (Kashi)'],
      packages: [
        { id: 'pkg4_1', name: 'Private Bajra (Up to 5 Persons)', price: 3500, durationHours: 2, numberOfPandits: 1, inclusions: ['Ganga Puja kit', '11 Lamps', 'Ganga Jal Prasad'] },
        { id: 'pkg4_2', name: 'VIP Grand Bajra (Up to 15 Persons)', price: 8900, durationHours: 3, numberOfPandits: 2, inclusions: ['Vedic Aarti on Boat', '21 Lamps', 'Ganga Jal + Sweets Box', 'Professional Photographer'] }
      ]
    },
    {
      id: 'p5',
      name: 'Griha Pravesh (House Warming) Vedic Puja',
      slug: 'griha-pravesh-puja',
      category: 'Puja at Home',
      categorySlug: 'puja-at-home',
      shortDescription: 'Invite peace, fortune, and positive energy into your new home with Vaastu Shanti & Ganesha Puja.',
      description: 'A special ceremony performed before moving into a new residence. It involves cleansing the premises of negative energies using Vaastu Puja, Navgraha Havan, and boiling of milk to represent prosperity and abundance.',
      significance: 'Invokes divine vibrations to protect the home and its dwellers from future misfortunes.',
      process: '1. Dwani (Entry threshold) Puja\n2. Ganesha & Gauri Stuti\n3. Kalash Sthapana\n4. Vaastu Shanti Havan\n5. Boiling Milk Ceremony\n6. Mahaprasad distribution',
      startingPrice: 6500,
      rating: 4.85,
      reviewCount: 224,
      isFeatured: false,
      isLive: false,
      thumbnailUrl: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=800',
      availableCities: ['Bangalore', 'Hyderabad', 'Pune', 'Varanasi (Kashi)'],
      packages: [
        { id: 'pkg5_1', name: 'Standard Griha Pravesh', price: 6500, durationHours: 3, numberOfPandits: 1, inclusions: ['Ganesha Puja', 'Navgraha Havan', 'Cleanse materials'] },
        { id: 'pkg5_2', name: 'Premium (Vaastu & Satyanarayan)', price: 12500, durationHours: 5, numberOfPandits: 2, inclusions: ['Complete Havan', 'Satyanarayan Katha', 'Premium dry fruits', 'Saffron Toran'] }
      ]
    },
    {
      id: 'p6',
      name: 'Panchamrit Shringar & Dugdhabhishek',
      slug: 'panchamrit-shringar-abhishek',
      category: 'Abhishekam',
      categorySlug: 'abhishekam',
      shortDescription: 'Ritualistic Shivalinga bathing with five sacred elements followed by holy flower decoration.',
      description: 'Panchamrit is a mix of milk, curd, honey, ghee, and sugar. Offering this to the deity represents absolute devotion and cleanses the devotee\'s heart. A beautiful floral shringar (decoration) is done after the abhishek.',
      significance: 'Brings immense mental peace, removes domestic troubles, and satisfies Lord Shiva.',
      process: '1. Initial water wash\n2. Dugdha (Milk) & Curd wash\n3. Honey, Ghee, Sugar coating\n4. Gangajal purification\n5. Grand flower decoration',
      startingPrice: 2500,
      rating: 4.9,
      reviewCount: 112,
      isFeatured: false,
      isLive: true,
      thumbnailUrl: 'https://images.unsplash.com/photo-1544984243-ec57ea16fe25?auto=format&fit=crop&q=80&w=800',
      availableCities: ['Varanasi (Kashi)', 'Deoghar (Baidyanath)', 'Ujjain (Mahakal)'],
      packages: [
        { id: 'pkg6_1', name: 'Standard Abhishek', price: 2500, durationHours: 1, numberOfPandits: 1, inclusions: ['Panchamrit kit', 'Bilvapatra (108)', 'Basic Prasad'] },
        { id: 'pkg6_2', name: 'Maha Abhishek & Shringar', price: 5500, durationHours: 2, numberOfPandits: 2, inclusions: ['Vishesh Panchamrit', 'Grand Flower Shringar', 'Video call link for live viewing', 'Rudraksha box'] }
      ]
    }
  ];

  // ── Cart State ──
  private cartItems = new BehaviorSubject<CartItem[]>([]);
  cartItems$ = this.cartItems.asObservable();

  constructor() {
    // Load initial cart from storage if any
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('kashivedic_cart');
      if (stored) {
        this.cartItems.next(JSON.parse(stored));
      }
    }
  }

  // ── Getters ──
  getPujas() {
    return [...this.pujas];
  }

  getFeaturedPujas() {
    return this.pujas.filter(p => p.isFeatured);
  }

  getPujaBySlug(slug: string): Puja | undefined {
    return this.pujas.find(p => p.slug === slug);
  }

  getPujasByCategory(categorySlug: string): Puja[] {
    return this.pujas.filter(p => p.categorySlug === categorySlug);
  }

  // ── Cart Operations ──
  addToCart(item: Omit<CartItem, 'id'>) {
    const items = [...this.cartItems.value];
    const newItem: CartItem = {
      ...item,
      id: 'cart_' + Math.random().toString(36).substr(2, 9)
    };
    items.push(newItem);
    this.updateCart(items);
  }

  removeFromCart(id: string) {
    const items = this.cartItems.value.filter(item => item.id !== id);
    this.updateCart(items);
  }

  clearCart() {
    this.updateCart([]);
  }

  private updateCart(items: CartItem[]) {
    this.cartItems.next(items);
    if (typeof window !== 'undefined') {
      localStorage.setItem('kashivedic_cart', JSON.stringify(items));
    }
  }

  getCartTotal(): number {
    return this.cartItems.value.reduce((sum, item) => sum + item.selectedPackage.price, 0);
  }

  getGst(): number {
    return Math.round(this.getCartTotal() * 0.18);
  }

  getGrandTotal(): number {
    return this.getCartTotal() + this.getGst() + 99; // ₹99 Prasad courier fee
  }
}
